import DownloadIcon from '@mui/icons-material/Download';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import StopIcon from '@mui/icons-material/Stop';
import { Box, Button } from '@mui/material';
import React from 'react';
import { useMidiContext } from 'Contexts/MidiContext';
import { FeatureFlag, useFeatureFlag } from 'Utils/FeatureFlags';
import {
  LOCAL_AUDIO_BRIDGE_EVENT,
  LOCAL_AUDIO_GRADE_EVENT,
  type LocalAudioBridgeEventDetail,
  type LocalAudioGradeEventDetail,
} from 'Utils/LocalAudioSessionEvents';
import { useAudioRecognition } from '../AudioRecognitionContext';
import type { RecognitionFrame } from '../types';
import {
  buildSightReadingTestFrame,
  buildSightReadingTestReport,
  SIGHT_READING_TEST_MAX_FRAMES,
  type SightReadingExpectedMidiSnapshot,
  type SightReadingTestFrame,
  type SightReadingTestReport,
} from '../testing/sightReadingSession';
import { AUDIO_SESSION_WAVEFORM_INTERVAL_MS } from '../testing/waveformCapture';
import {
  buildSightReadingLiveFrame,
  buildSightReadingLiveJudgment,
  type SightReadingLiveFrame,
  type SightReadingLiveJudgment,
} from '../testing/sightReadingLiveDiagnostics';
import SightReadingLiveReadout from './SightReadingLiveReadout';

const UI_UPDATE_INTERVAL_MS = 250;

type SightReadingTestRecorderProps = {
  exercisePlaying: boolean;
  getExpectedMidiSnapshot: () => SightReadingExpectedMidiSnapshot;
  context: Record<string, unknown>;
};

const formatPercent = (value: number) => `${Math.round(value * 100)}%`;

const formatLatency = (value: number | null) => (value == null ? 'n/a' : `${Math.round(value)} ms`);

const downloadReport = (report: SightReadingTestReport) => {
  const blob = new Blob([JSON.stringify(report, null, 2)], {
    type: 'application/json;charset=utf-8',
  });
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = `sight-reading-audio-test-${report.createdAtIso.replace(/[:.]/g, '-')}.json`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
};

const recorderButtonSx = {
  minHeight: '30px',
  borderRadius: '999px',
  paddingX: '10px',
  textTransform: 'none',
  fontSize: '11px',
  fontWeight: 800,
} as const;

const SightReadingTestRecorder = ({
  exercisePlaying,
  getExpectedMidiSnapshot,
  context,
}: SightReadingTestRecorderProps) => {
  const enabled = useFeatureFlag(FeatureFlag.USE_AUDIO_RECOGNITION);
  const { addListener, removeListener, calibrationProfile, running } = useAudioRecognition();
  const { preferredInputMode, audioInputLatencyMs, openInputSetupModal } = useMidiContext();
  const [recording, setRecording] = React.useState(false);
  const [capturedFrameCount, setCapturedFrameCount] = React.useState(0);
  const [report, setReport] = React.useState<SightReadingTestReport | null>(null);
  const [limitReached, setLimitReached] = React.useState(false);
  const [liveFrame, setLiveFrame] = React.useState<SightReadingLiveFrame | null>(null);
  const [activeBridgeMidiNotes, setActiveBridgeMidiNotes] = React.useState<number[]>([]);
  const [liveJudgments, setLiveJudgments] = React.useState<SightReadingLiveJudgment[]>([]);
  const recordingRef = React.useRef(false);
  const framesRef = React.useRef<SightReadingTestFrame[]>([]);
  const bridgeEventsRef = React.useRef<LocalAudioBridgeEventDetail[]>([]);
  const gradeEventsRef = React.useRef<LocalAudioGradeEventDetail[]>([]);
  const activeBridgeMidiNotesRef = React.useRef(new Set<number>());
  const liveFrameRef = React.useRef<SightReadingLiveFrame | null>(null);
  const startedAtMsRef = React.useRef(0);
  const exercisePlayingRef = React.useRef(exercisePlaying);
  const getExpectedMidiSnapshotRef = React.useRef(getExpectedMidiSnapshot);
  const contextRef = React.useRef(context);
  const sessionMetadataRef = React.useRef<Record<string, unknown>>({});
  const sessionCalibrationProfileRef = React.useRef(calibrationProfile);
  const lastUiUpdateAtMsRef = React.useRef(0);
  const lastWaveformCaptureAtMsRef = React.useRef(0);

  exercisePlayingRef.current = exercisePlaying;
  getExpectedMidiSnapshotRef.current = getExpectedMidiSnapshot;
  contextRef.current = context;

  const finishRecording = React.useCallback(
    (endedAtMs = Date.now()) => {
      if (!recordingRef.current && framesRef.current.length === 0) return;
      recordingRef.current = false;
      setRecording(false);
      setCapturedFrameCount(framesRef.current.length);
      setReport(
        buildSightReadingTestReport(framesRef.current, {
          startedAtMs: startedAtMsRef.current,
          endedAtMs,
          calibrationProfile: sessionCalibrationProfileRef.current,
          metadata: sessionMetadataRef.current,
          bridgeEvents: [...bridgeEventsRef.current],
          gradeEvents: [...gradeEventsRef.current],
        }),
      );
    },
    [setRecording],
  );

  React.useEffect(() => {
    if (!enabled || process.env.REACT_APP_NODE_ENV === 'prd') return;

    const listener = {
      name: 'sight-reading-audio-test-recorder',
      onFrame: (frame: RecognitionFrame) => {
        if (!recordingRef.current) return;

        const capturedAtMs = Date.now();
        let expectedSnapshot: SightReadingExpectedMidiSnapshot = {
          midiNotes: [],
        };
        try {
          expectedSnapshot = getExpectedMidiSnapshotRef.current();
        } catch (error) {
          console.warn(
            '[AudioRecognition] Sight-reading test could not read expected notes.',
            error,
          );
        }

        const nextLiveFrame = buildSightReadingLiveFrame(frame, expectedSnapshot);
        liveFrameRef.current = nextLiveFrame;
        const captureWaveform =
          capturedAtMs - lastWaveformCaptureAtMsRef.current >= AUDIO_SESSION_WAVEFORM_INTERVAL_MS;
        if (captureWaveform) {
          lastWaveformCaptureAtMsRef.current = capturedAtMs;
        }

        framesRef.current.push(
          buildSightReadingTestFrame(
            frame,
            expectedSnapshot,
            exercisePlayingRef.current,
            capturedAtMs,
            captureWaveform,
          ),
        );

        if (capturedAtMs - lastUiUpdateAtMsRef.current >= UI_UPDATE_INTERVAL_MS) {
          lastUiUpdateAtMsRef.current = capturedAtMs;
          setCapturedFrameCount(framesRef.current.length);
          setLiveFrame(nextLiveFrame);
        }

        if (framesRef.current.length >= SIGHT_READING_TEST_MAX_FRAMES) {
          setLimitReached(true);
          finishRecording(capturedAtMs);
        }
      },
      wantsWaveform: true,
    };

    addListener(listener);
    return () => removeListener(listener);
  }, [addListener, enabled, finishRecording, removeListener]);

  React.useEffect(() => {
    if (!enabled || process.env.REACT_APP_NODE_ENV === 'prd') return;

    const onBridgeEvent = (event: Event) => {
      if (!recordingRef.current) return;
      const detail = (event as CustomEvent<LocalAudioBridgeEventDetail>).detail;
      bridgeEventsRef.current.push(detail);
      if (detail.type === 'note-on') {
        activeBridgeMidiNotesRef.current.add(detail.midiNote);
      } else {
        activeBridgeMidiNotesRef.current.delete(detail.midiNote);
      }
      setActiveBridgeMidiNotes(
        Array.from(activeBridgeMidiNotesRef.current).sort((left, right) => left - right),
      );
    };

    const onGradeEvent = (event: Event) => {
      if (!recordingRef.current) return;
      const detail = (event as CustomEvent<LocalAudioGradeEventDetail>).detail;
      gradeEventsRef.current.push(detail);
      if (
        detail.phase === 'offset' &&
        detail.state !== 'EARLY_OFFSET' &&
        detail.state !== 'LATE_OFFSET'
      ) {
        return;
      }
      const judgment = buildSightReadingLiveJudgment(detail, liveFrameRef.current);
      setLiveJudgments((current) => [judgment, ...current].slice(0, 5));
    };

    window.addEventListener(LOCAL_AUDIO_BRIDGE_EVENT, onBridgeEvent);
    window.addEventListener(LOCAL_AUDIO_GRADE_EVENT, onGradeEvent);
    return () => {
      window.removeEventListener(LOCAL_AUDIO_BRIDGE_EVENT, onBridgeEvent);
      window.removeEventListener(LOCAL_AUDIO_GRADE_EVENT, onGradeEvent);
    };
  }, [enabled]);

  React.useEffect(
    () => () => {
      recordingRef.current = false;
    },
    [],
  );

  if (!enabled || process.env.REACT_APP_NODE_ENV === 'prd') return null;

  const ready =
    preferredInputMode === 'audio' && calibrationProfile != null && running && !recording;
  const evaluation = report?.evaluation;

  const startRecording = () => {
    const startedAtMs = Date.now();
    framesRef.current = [];
    bridgeEventsRef.current = [];
    gradeEventsRef.current = [];
    activeBridgeMidiNotesRef.current.clear();
    liveFrameRef.current = null;
    startedAtMsRef.current = startedAtMs;
    lastUiUpdateAtMsRef.current = startedAtMs;
    lastWaveformCaptureAtMsRef.current = startedAtMs - AUDIO_SESSION_WAVEFORM_INTERVAL_MS;
    sessionCalibrationProfileRef.current = calibrationProfile;
    sessionMetadataRef.current = {
      ...contextRef.current,
      audioInputLatencyMs,
      userAgent: window.navigator.userAgent,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
      },
    };
    setLimitReached(false);
    setReport(null);
    setCapturedFrameCount(0);
    setLiveFrame(null);
    setActiveBridgeMidiNotes([]);
    setLiveJudgments([]);
    recordingRef.current = true;
    setRecording(true);
  };

  return (
    <Box
      data-sight-reading-audio-test-recorder
      sx={{
        position: 'fixed',
        top: { xs: '78px', md: '92px' },
        right: { xs: '8px', md: '14px' },
        zIndex: 2800,
        width: { xs: 'min(330px, calc(100vw - 16px))', md: '330px' },
        borderRadius: '12px',
        border: recording
          ? '1px solid rgba(255, 112, 112, 0.7)'
          : '1px solid rgba(121, 216, 255, 0.3)',
        backgroundColor: 'rgba(5, 11, 19, 0.96)',
        boxShadow: '0 12px 36px rgba(0, 0, 0, 0.32)',
        color: '#EAF2FF',
        padding: '10px 12px',
        fontFamily: 'Lato, sans-serif',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
        }}
      >
        <Box>
          <Box sx={{ fontSize: '12px', fontWeight: 900 }}>Sight-reading audio test</Box>
          <Box sx={{ marginTop: '2px', color: 'rgba(234, 242, 255, 0.68)', fontSize: '10px' }}>
            {recording
              ? `Recording ${capturedFrameCount} frames${exercisePlaying ? ' · exercise active' : ' · waiting for play'}`
              : preferredInputMode !== 'audio'
                ? 'Select Acoustic Piano input'
                : !calibrationProfile
                  ? 'Calibration required'
                  : !running
                    ? 'Microphone is not running'
                    : report
                      ? `Stopped · ${report.frameCount} frames`
                      : 'Ready before you press Play'}
          </Box>
        </Box>
        {recording && (
          <Box
            aria-label="Recording"
            sx={{
              width: '9px',
              height: '9px',
              flex: '0 0 auto',
              borderRadius: '50%',
              backgroundColor: '#FF7070',
              boxShadow: '0 0 0 4px rgba(255, 112, 112, 0.12)',
            }}
          />
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px' }}>
        {!recording && (
          <Button
            variant="contained"
            size="small"
            startIcon={<FiberManualRecordIcon />}
            onClick={startRecording}
            disabled={!ready}
            sx={recorderButtonSx}
          >
            Start test
          </Button>
        )}
        {recording && (
          <Button
            variant="contained"
            color="error"
            size="small"
            startIcon={<StopIcon />}
            onClick={() => finishRecording()}
            sx={recorderButtonSx}
          >
            Stop test
          </Button>
        )}
        {!recording && preferredInputMode !== 'audio' && (
          <Button
            variant="outlined"
            size="small"
            onClick={openInputSetupModal}
            sx={{ ...recorderButtonSx, color: '#EAF2FF', borderColor: 'rgba(234,242,255,0.28)' }}
          >
            Input setup
          </Button>
        )}
        {!recording && preferredInputMode === 'audio' && (
          <Button
            data-audio-test-recalibrate
            variant="outlined"
            size="small"
            onClick={openInputSetupModal}
            sx={{ ...recorderButtonSx, color: '#EAF2FF', borderColor: 'rgba(234,242,255,0.28)' }}
          >
            {calibrationProfile ? 'Recalibrate' : 'Calibration'}
          </Button>
        )}
        {report && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={() => downloadReport(report)}
            sx={{ ...recorderButtonSx, color: '#EAF2FF', borderColor: 'rgba(121,216,255,0.38)' }}
          >
            Export JSON
          </Button>
        )}
      </Box>

      {evaluation && (
        <Box
          aria-live="polite"
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: '5px 10px',
            marginTop: '8px',
            paddingTop: '8px',
            borderTop: '1px solid rgba(234, 242, 255, 0.12)',
            color: 'rgba(234, 242, 255, 0.82)',
            fontSize: '10px',
          }}
        >
          <Box>Product-path F1: {formatPercent(evaluation.f1)}</Box>
          <Box>Above-gate F1: {formatPercent(evaluation.aboveGateF1)}</Box>
          <Box>
            Exact expectations: {evaluation.recognizedExpectationCount}/
            {evaluation.expectationCount}
          </Box>
          <Box>Signal latency: {formatLatency(evaluation.medianSignalToDecisionLatencyMs)}</Box>
          <Box>Missed after signal: {evaluation.missedAfterSignalCount}</Box>
          <Box>No signal: {evaluation.noSignalExpectationCount}</Box>
        </Box>
      )}

      {(recording || report) && (
        <SightReadingLiveReadout
          frame={liveFrame}
          activeBridgeMidiNotes={activeBridgeMidiNotes}
          judgments={liveJudgments}
        />
      )}

      {limitReached && (
        <Box sx={{ marginTop: '7px', color: '#FFD27D', fontSize: '10px' }}>
          The recorder stopped at {SIGHT_READING_TEST_MAX_FRAMES} frames. Export this run before
          starting another.
        </Box>
      )}
    </Box>
  );
};

export default SightReadingTestRecorder;
